/**
 * The words the mapping screen puts on each field: the friendly label, with the GS1 term
 * under it where the standard has one (`screens.md` §7: *"this is where the vocabulary gets
 * taught"*). `gtin14`'s AI 01 and the batch fields' AIs 10, 17 and 11 are the standard's; the
 * attribute keys are their own CURIEs.
 */
import {
  type IMPORT_ATTRIBUTE_KEYS,
  type IMPORT_BATCH_FIELDS,
  IMPORT_LANG_FIELDS,
  type IMPORT_PRODUCT_FIELDS,
} from "@packagepage/shared";

type PlainField =
  | (typeof IMPORT_PRODUCT_FIELDS)[number]
  | (typeof IMPORT_BATCH_FIELDS)[number]
  | (typeof IMPORT_ATTRIBUTE_KEYS)[number];

interface FieldWords {
  readonly label: string;
  readonly term: string | null;
}

/** Total over every field that is not language-tagged, so a field added to the registry fails to compile here. */
const PLAIN: Readonly<Record<PlainField, FieldWords>> = {
  gtin14: { label: "GTIN", term: "GTIN · AI 01" },
  brandName: { label: "Brand", term: null },
  sku: { label: "SKU", term: null },
  gpcCategory: { label: "GPC category", term: null },
  netContent: { label: "Net content", term: null },
  "gs1:allergenStatement": { label: "Allergen statement", term: "gs1:allergenStatement" },
  "gs1:ingredientStatement": {
    label: "Ingredient statement",
    term: "gs1:ingredientStatement",
  },
  "gs1:nutrientBasisQuantity": {
    label: "Nutrient basis quantity",
    term: "gs1:nutrientBasisQuantity",
  },
  "gs1:netContent": { label: "Net content, as a GS1 attribute", term: "gs1:netContent" },
  "gs1:countryOfOrigin": { label: "Country of origin", term: "gs1:countryOfOrigin" },
  "gs1:storageHandling": { label: "Storage and handling", term: "gs1:storageHandling" },
  "batch.lot": { label: "Batch lot", term: "Batch or lot number · AI 10" },
  "batch.expiry": { label: "Batch best-before", term: "Expiration date · AI 17" },
  "batch.mfgDate": { label: "Batch made on", term: "Production date · AI 11" },
};

const LANGUAGE_GROUPS: Readonly<Record<(typeof IMPORT_LANG_FIELDS)[number], string>> = {
  name: "Name",
  description: "Description",
};

function languageName(tag: string): string {
  try {
    return new Intl.DisplayNames(["en"], { type: "language" }).of(tag) ?? tag;
  } catch {
    return tag;
  }
}

export function fieldWords(field: string): FieldWords {
  const plain = (PLAIN as Readonly<Record<string, FieldWords | undefined>>)[field];
  if (plain !== undefined) return plain;
  const dot = field.indexOf(".");
  const group = (LANGUAGE_GROUPS as Readonly<Record<string, string | undefined>>)[
    field.slice(0, dot)
  ];
  if (dot > 0 && group !== undefined) {
    return { label: `${group} · ${languageName(field.slice(dot + 1))}`, term: null };
  }
  return { label: field, term: null };
}

export interface FieldOption {
  readonly value: string;
  readonly label: string;
}

/** Every field a column may be matched to, English for the two language fields. A field outside the list stays selectable when a column already holds it. */
export function fieldOptions(current: readonly (string | null)[]): FieldOption[] {
  const values = [
    ...(Object.keys(PLAIN) as PlainField[]),
    ...IMPORT_LANG_FIELDS.map((f) => `${f}.en`),
  ];
  for (const field of current) {
    if (field !== null && !values.includes(field)) values.push(field);
  }
  return values.map((value) => ({ value, label: fieldWords(value).label }));
}
